import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationStart } from '@angular/router';
import { IResponseMeta, RequestService } from '@df/api/request/request.service';
import { Basket as NBasket } from '@df/basket/basket.interfaces';
import { LocalStorage } from '@df/browser/storage/decorator/local-storage';
import { SessionStorage } from '@df/browser/storage/decorator/session-storage';
import { PRODUCT_VIEW_MODEL_CHANGE_EVENT_NAME } from '@df/catalog-ui/product/view/product-view.component.def';
import { ICrumb, Model } from '@df/catalog/model';
import { IProductDataV2, IProductDataV3, Product } from '@df/catalog/product/product';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { PrivacyStateService } from '@df/core/meta/privacy-state.service';
import { IEventType } from '@df/core/service/df-event';
import { GtmProvider } from '@df/module-gtm/gtm-provider.service';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { TimeoutService } from '@df/ng/timeout.service';
import { Basket } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { Customer } from '@df/session/api/customer';
import { CustomerModel } from '@df/session/api/customer.model';
import { Login } from '@df/session/login/login.interfaces';
import { NewsletterChangeEvent } from '@df/session/newsletter/newsletter-change.event';
import { NewsletterUpdateEvent } from '@df/session/newsletter/newsletter-update.event';
import { Newsletter } from '@df/session/newsletter/newsletter.interfaces';
import { NewsletterService } from '@df/session/newsletter/newsletter.service';
import { Prefs } from '@df/session/prefs';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import cloneDeep from 'lodash-es/cloneDeep';
import compact from 'lodash-es/compact';
import defaults from 'lodash-es/defaults';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import isString from 'lodash-es/isString';
import isUndefined from 'lodash-es/isUndefined';
import map from 'lodash-es/map';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import take from 'lodash-es/take';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NModuleGtm } from './df-module-gtm.config';
import './integration/gtm-inclusion';

declare module '@df/core/service/df-event' {
    interface IEventType {
        // gtm
        dfGtmEvent: DfEventConfig<
            'dfGtmEvent',
            {
                eventData: Gtm.IEvent;
            },
            void
        >;
        productClick: DfEventConfig<
            'productClick',
            {
                product: Product<IProductDataV2>;
                position?: number;
                listId?: string;
                listName?: string;
            },
            void
        >;
    }
}

export class GTMServiceEvent {
    constructor(public eventData: Gtm.IEvent) {}
}

export class GTMServiceEventAfter {
    constructor(public eventData: Gtm.IEvent) {}
}

export class GTMServiceProductData {
    constructor(
        public data: Gtm.IProductImpressionData,
        public product: Product<IProductDataV2>
    ) {}
}

export class GTMServiceEcommerceItem {
    constructor(
        public data: Gtm.IEcommerceItem,
        public product: Product<IProductDataV2>
    ) {}
}

///////////////////////// BACKEND EVENT ////////////////////////////

/**
 * Service event instance for events coming from BE
 */
export class GTMServiceTrackBackendEvent extends GTMServiceEvent {}

/**
 * Service event instance for before begin checkout send.
 */
export class GtmServiceBeginCheckoutEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventBeginCheckout) {
        super(eventData);
    }
}

/**
 * Service event instance for before payment method save.
 */
export class GtmServiceAddPaymentInfoEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventAddPaymentInfo) {
        super(eventData);
    }
}

/**
 * Service event instance for before shipping method save.
 */
export class GtmServiceAddShipmentInfoEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventAddShipmentInfo) {
        super(eventData);
    }
}

export class GtmServiceSelectItemEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventSelectItem) {
        super(eventData);
    }
}

export class GtmServiceViewCartEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventViewCart) {
        super(eventData);
    }
}

export class GtmServiceViewItemEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventViewItem) {
        super(eventData);
    }
}

export class GtmServiceViewItemListEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventViewItemList) {
        super(eventData);
    }
}

export class GtmServiceNewsletterEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventNewsletter) {
        super(eventData);
    }
}

/**
 * Service event instance for before content view send
 */
export class GTMServiceTrackContentViewEvent extends GTMServiceEvent {
    constructor(
        public override eventData: Gtm.IEventContentView,
        public model?: Model
    ) {
        super(eventData);
    }
}

///////////////////////// PRODUCT CLICK ///////////////////////////

/**
 * Service event instance for before product view send
 */
export class GTMServiceTrackProductClickEvent extends GTMServiceEvent {
    constructor(
        public override eventData: Gtm.IEventProductClick,
        public model: Model
    ) {
        super(eventData);
    }
}

///////////////////////// PROMOTION CLICK ///////////////////////////

/**
 * Service event instance for before promotio click send
 */
export class GTMServiceTrackPromotionClickEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventPromotionClick) {
        super(eventData);
    }
}

///////////////////////// PROMOTION CLICK ///////////////////////////

/**
 * Product click event data
 */

/**
 * Service event instance for before promotio impression send
 */
export class GTMServiceTrackPromotionImpressionEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventPromotionImpression) {
        super(eventData);
    }
}

///////////////////////// IMPRESSIONS  ///////////////////////////

/**
 * Product click event data
 */

/**
 * Service event instance for before promotio impression send
 */
export class GTMServiceTrackImpressionsEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventImpressions) {
        super(eventData);
    }
}

///////////////////////// CUSTOMER UPDATE  ///////////////////////////

/**
 * Product click event data
 */

/**
 * Service event instance for before promotio impression send
 */
export class GTMServiceTrackCustomerUpdateEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventCustomerUpdate) {
        super(eventData);
    }
}

///////////////////////// CUSTOMER EMAIL  ///////////////////////////

/**
 * Custoemr emeila provided event data
 */

/**
 * Service event instance for before promotio impression send
 */
export class GTMServiceTrackCustomerEmailEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventCustomerEmail) {
        super(eventData);
    }
}

///////////////////////// CHECKOUT STEP  ///////////////////////////

/**
 * Checkout event (to measure checkout steps)
 * https://developers.google.com/tag-manager/enhanced-ecommerce#checkoutstep
 */

/**
 * Service event instance for before promotio impression send
 */
export class GTMServiceTrackCheckoutStepEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventCheckout) {
        super(eventData);
    }
}

///////////////////////// CHECKOUT STEP  ///////////////////////////

/**
 * Checkout option event (to measure checkout steps changes)
 * https://developers.google.com/tag-manager/enhanced-ecommerce#checkout_option
 */

/**
 * Service event instance for before checkout option event
 */
export class GTMServiceTrackCheckoutOptionEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventOptionCheckout) {
        super(eventData);
    }
}

///////////////////////// INTERACTION EVENT  ///////////////////////////

/**
 * Service event instance for interaction event
 */
export class GTMServiceTrackInteractionEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventRegular) {
        super(eventData);
    }
}

///////////////////////// NON INTERACTION EVENT  ///////////////////////////

/**
 * Service event instance for non interaction event
 */
export class GTMServiceTrackNonInteractionEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventRegular) {
        super(eventData);
    }
}

///////////////////////// NON INTERACTION EVENT  ///////////////////////////

/**
 * Basket data event data
 */

/**
 * Service event instance for basket chagnge event
 */
export class GTMServiceTrackBasketChangeEvent extends GTMServiceEvent {
    constructor(public override eventData: Gtm.IEventBasket) {
        super(eventData);
    }
}

let contentCount = 1;

///////////////////////////////////////////////////////////////////
///
///
/// GTM SERVICE
///
///
///////////////////////////////////////////////////////////////////
@Injectable({
    providedIn: 'root'
})
export class GtmService implements OnDestroy {
    /**
     * Event subject to observe for service events
     *
     * @deprecated Please use DfEventService instead
     */
    events = new Subject<GTMServiceEvent>();

    /**
     * Event subject to observe for product data to change data in fly
     */
    productData = new Subject<GTMServiceProductData>();

    /**
     * Event subject to observe for ecommerce items to change data in fly
     */
    ecommerceItem = new Subject<GTMServiceEcommerceItem>();

    /**
     * Last path we tracked content view for
     */
    protected lastPath?: string;

    /**
     * Last item we tracked view for
     */
    protected lastViewItemPath?: string;

    /**
     * Last QTY of basket when tracking
     */
    protected lastBasketQty?: number;

    private lastCustomerEmail?: string;

    /**
     * Buffer for mass impressions not to send separately
     */
    protected productImpressionsBuffer: Gtm.IProductImpressionData[] = [];

    /**
     * Buffer for checkout success event
     */
    protected checkoutSuccessBufferCallback?: () => void;

    /**
     * Last quote id used with checkout
     */
    @SessionStorage('gtm.lastCheckoutQuote')
    protected lastCheckoutQuote?: number;

    /**
     * Last quote id used with checkout
     */
    @SessionStorage('gtm.maxTrackedCheckoutStep')
    protected maxTrackedCheckoutStep?: number;

    @LocalStorage('gtm.checkoutBeforeSubmitData')
    protected checkoutBeforeSubmitData: Gtm.ICheckoutSubmitData | undefined;

    /**
     * This structure describes checkout steps (maps step name to index). This map is loaded from local config
     * or can be generated dynamically based on step's order property. If generated dynamically, this can be
     * re-generated each time the checkout structure changes.
     * @protected
     */
    protected checkoutStepsStructure: { [key: string]: number } = {};

    /**
     * Events map
     */
    public eventsMap!: {
        addPaymentInfo: string;
        addShipmentInfo: string;
        beginCheckout: string;
        eventGA4: string;
        selectItem: string;
        viewItem: string;
        viewItemList: string;
        // other events
        newsletter: string;
        basketChange: string;
        checkout: string;
        checkoutOption: string;
        checkoutSuccess: string;
        contentView: string;
        customerEmailProvided: string;
        customerUpdate: string;
        enhancedEvent: string;
        interaction: string;
        nonInteraction: string;
        productClick: string;
        productOptionSelected: string;
        promotionClick: string;
        promotionImpression: string;
        tierCookieConsentChange: string;
    } & { [key in Gtm.EEvent]?: string };

    protected subscriptions = new Subscription();

    protected trackImpressionsThrotled!: () => void;

    protected newsletterService = inject(NewsletterService);

    protected meta = inject(Meta);
    protected prefs = inject(Prefs);
    protected app = inject(App);
    protected customer = inject(Customer);
    protected location = inject(Location);
    protected customerModel = inject(CustomerModel);
    protected basket = inject(Basket);
    protected requestService = inject(RequestService);
    protected timeoutService = inject(TimeoutService);
    protected gtmProvider = inject(GtmProvider);
    private moduleConfig = inject(NModuleGtm.OPTIONS);
    private privacyStateService = inject(PrivacyStateService);

    appInitialize() {
        this.trackImpressionsThrotled = this.timeoutService.throttle(
            () => {
                this.trackImpressions();
            },
            1000,
            { leading: false }
        );

        this.registerEvents();

        this.setListeners();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Register events name.
     * This is a separate method to allow extending classes to add their own events.
     *
     * @example - Change the `contentView` event name in local decorator
     * ```typescript
     * protected registerEvents(): void {
     *   super.registerEvents();
     *
     *   this.eventsMap = {
     *     ...this.eventsMap,
     *     contentView: 'content.view'
     *   };
     * }
     * ```
     *
     * @info Ready for GA4
     */
    protected registerEvents(): void {
        this.eventsMap = {
            ...this.eventsMap,
            // ready for GA4
            addPaymentInfo: 'add_payment_info_ga4',
            addShipmentInfo: 'add_shipment_info_ga4',
            beginCheckout: 'begin_checkout_ga4',
            eventGA4: 'ga4event',
            selectItem: 'select_item_ga4',
            viewCart: 'view_cart_ga4',
            viewItem: 'view_item_ga4',
            viewItemList: 'view_item_list_ga4',
            // other events
            newsletter: 'newsletter',
            basketChange: 'basket-change',
            checkout: 'checkout',
            checkoutOption: 'checkoutOption',
            checkoutSuccess: 'checkoutSuccess',
            contentView: 'content-view',
            customerEmailProvided: 'customerEmailProvided',
            customerUpdate: 'customer-update',
            enhancedEvent: 'enhancedEvent',
            interaction: 'interaction',
            nonInteraction: 'non-interaction',
            productClick: 'productClick',
            productOptionSelected: 'product-option-selected',
            promotionClick: 'promotionClick',
            promotionImpression: 'promotionImpression',
            tierCookieConsentChange: 'tier_cookie_consent_change'
        };
    }

    /**
     * Sets listeners for tracking.
     *
     * @info Ready for GA4
     */
    protected setListeners(): void {
        // ready for GA4
        this.setAddPaymentInfoTracking();
        this.setAddShipmentInfoTracking();
        this.setBackendTracking();
        this.setCheckoutTracking();
        this.setViewCartTracking();
        this.setViewItemTracking();

        // other events
        this.setNewsletterTracking();
        this.setCheckoutOldTracking();
        this.setContentViewTracking();
        this.setBasketChangeTracking();
        this.setCustomerEmailTracking();
        this.setProductChangeTracking();
        this.setImpressionsTracking();
        this.setSearchTracking();
        this.setDfEventTracking();
    }

    //////////////////////////////////////////////////////
    ///
    ///
    /// TRACKING
    ///
    ///
    //////////////////////////////////////////////////////
    private setDfEventTracking(): void {
        this.subscriptions.add(
            this.app.event.success(event => {
                switch (event.id) {
                    case 'nexusCustomerRegister':
                        this.onRegistrationRequest(event);
                        break;
                    case 'productClick':
                        this.trackProductClick(
                            event.data.product,
                            event.data.listName || event.data.listId || this.getDefaultListName(),
                            event.data.position
                        );
                        break;
                }
            })
        );
        this.subscriptions.add(
            this.privacyStateService.selection$.subscribe(selection => {
                if (selection) {
                    this.push({ event: this.eventsMap.tierCookieConsentChange, tco_tier_cookie_consent: selection } as Gtm.IEvent);
                }
            })
        );
    }

    /**
     * Set up listener for add payment info
     *
     * @info - Ready for GA4
     */
    protected setAddPaymentInfoTracking(): void {
        this.subscriptions.add(
            this.app.events
                .on(Checkout.EVENTS.SavePaymentMethodBefore)
                .subscribe((data: Checkout.ISavePaymentMethodEventData) => this.trackSavePaymentMethodBefore(data))
        );
    }

    /**
     * Set up listener for add shipment info
     *
     * @info - Ready for GA4
     */
    protected setAddShipmentInfoTracking(): void {
        this.subscriptions.add(
            this.app.events
                .on(Checkout.EVENTS.SaveShippingMethodBefore)
                .subscribe((data: Checkout.ISaveShippingMethodData) => this.trackSaveShipmentMethodBefore(data))
        );
    }

    /**
     * Set up listener for backend events
     *
     * @info - Ready for GA4
     */
    protected setBackendTracking(): void {
        // request service subscription for BE events
        this.requestService.subscribe(response => {
            const result = response?.result;

            if (result?.gtm) {
                (compact(Array.isArray(result.gtm) ? result.gtm : [result.gtm]) as Gtm.IEvent[]).forEach((data: Gtm.IEvent) =>
                    this.trackBackendEvent(data, response)
                );
            }
        });
    }

    /**
     * Set up listener for checkout changes
     *
     * @info Ready for GA4
     */
    protected setCheckoutTracking() {
        // `begin_checkout` event
        this.subscriptions.add(this.app.events.on(Checkout.EVENTS.Init).subscribe(() => this.trackBeginCheckout()));
    }

    /**
     * Set up listener for product view
     *
     * @info Ready for GA4
     */
    protected setViewItemTracking(): void {
        const onMetaChange = this.timeoutService.debounce(() => {
            try {
                const path = this.meta.getMetaProperty('url') || this.location.path();

                if (this.lastViewItemPath !== path) {
                    this.lastViewItemPath = path;
                    this.trackViewItem(this.meta.model as Product);
                }
            } catch (err) {
                this.app.logger.error(err);
            }
        }, 100);

        this.subscriptions.add(this.meta.subject.pipe(filter(metaState => metaState?.model?.type === 'product')).subscribe(onMetaChange));
    }

    /**
     * Set up listener for view cart
     *
     * @info Ready for GA4
     */
    protected setViewCartTracking(): void {
        // `basket.view` event
        this.subscriptions.add(this.app.events.on(NBasket.EVENTS.view).subscribe(() => this.trackViewCart()));
    }

    protected setNewsletterTracking(): void {
        this.subscriptions.add(
            this.newsletterService.newsletterStatus.subscribe(event => {
                const subscribeRequest = event.data as Newsletter.ISubscribeRequest;
                const placement = subscribeRequest?.source;

                if (event instanceof NewsletterChangeEvent) {
                    this.trackNewsletter(event.subscribed, event.email || this.customer.model.data.email, placement);
                } else if (event instanceof NewsletterUpdateEvent) {
                    this.trackNewsletter(this.customer.model.data.newsletter, this.customer.model.data.email, placement);
                }
            })
        );

        this.subscriptions.add(
            this.app.events
                .on(Checkout.EVENTS.SubmitBefore)
                .subscribe((checkoutSubmitBeforeData: Checkout.ISubmitBeforeEventData) =>
                    this.onCheckoutSubmitBefore(checkoutSubmitBeforeData)
                )
        );

        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.SuccessTracking).subscribe((successData: Checkout.ISuccessData) => {
                if (this.checkoutBeforeSubmitData) {
                    const checkoutBeforeSubmitData = cloneDeep(this.checkoutBeforeSubmitData);
                    this.checkoutBeforeSubmitData = undefined;

                    const { checkoutData } = checkoutBeforeSubmitData;

                    this.trackNewsletter(checkoutData.newsletter, successData.customer.email, 'checkout_success');
                }
            })
        );
    }

    /**
     * Set up listener for checkout changes
     */
    protected setCheckoutOldTracking() {
        /**
         * Listen to checkout
         *
         * 1. Each checkout step is tracked when on step-in (not step out)
         * 2. Step is not tracked twice in one session. We only track avance.
         * 3. Checkout options: shipping method, payment method are tracked everytime they change.
         *
         *
         * IMPORTANT
         * By default steps sequence matches exactly checkout-view steps. If you want to override numbers (like when
         * steps change during process), you need to pass config variable: gtm.checkout.steps  as string array.
         */
        this.app.events.on(Checkout.EVENTS.InitAfter).subscribe(data => {
            this.onCheckoutInitAfter(data);
        });

        this.app.events.on(Checkout.EVENTS.StepChange).subscribe(data => {
            this.onCheckoutStepChange(data);
        });

        this.app.events.on(Checkout.EVENTS.SaveShippingMethodBefore).subscribe((data: Checkout.IQuoteDataShipping) => {
            this.onShippingMethodChanged(data);
        });

        this.app.events.on(Checkout.EVENTS.SavePaymentMethodBefore).subscribe((data: Checkout.ISavePaymentMethodEventData) => {
            this.onPaymentMethodChanged(data);
        });

        this.app.events.on(Checkout.EVENTS.CheckoutGuestStateChanged).subscribe(data => {
            this.onGuestStatusChanged(data);
        });

        // prepare checkout step name => checkout step index map
        if (this.moduleConfig && this.moduleConfig.checkoutSteps) {
            this.checkoutStepsStructure = this.moduleConfig.checkoutSteps;
        } else {
            // Go automatic mode
            this.app.events.on<Checkout.IStepsSetUpdateEventData>(Checkout.EVENTS.StepsSetUpdate).subscribe(data => {
                // whenever a new step is registered or existing step is unregistered, rebuild the steps map

                const steps: string[] = [];

                for (const step in data.steps) {
                    if (Object.prototype.hasOwnProperty.call(data.steps, step)) {
                        steps[data.steps[step].order] = data.steps[step].name;
                    }
                }

                let i = 1;

                this.checkoutStepsStructure = {};

                steps.forEach(step => {
                    this.checkoutStepsStructure[step] = i++;
                });
            });
        }

        // subscribe to catch quote id changes to reset checkout process when changed
        this.customer.basket.subscribe(data => {
            if (!data) {
                return;
            }

            const quoteId = this.customer.basket.data.quote_id;

            if (quoteId !== this.lastCheckoutQuote) {
                this.lastCheckoutQuote = quoteId;
                this.maxTrackedCheckoutStep = 0;
            }
        });
    }

    /**
     * Set up listener for meta changes
     */
    protected setContentViewTracking(): void {
        const onMetaChange = this.timeoutService.debounce(() => {
            try {
                const path = this.meta.getMetaProperty('url') || this.location.path();

                if (this.lastPath !== path) {
                    this.lastPath = path;
                    this.trackContentView(path, this.meta.model, this.meta.state?.metaData || undefined);
                }
            } catch (err) {
                this.app.logger.error(err);
            }
        }, 100);

        // meta subscription for content view events
        this.subscriptions.add(this.meta.subscribe(onMetaChange));
    }

    protected setProductChangeTracking(): void {
        // Watch for product option selected
        this.subscriptions.add(
            this.app.events
                .on(PRODUCT_VIEW_MODEL_CHANGE_EVENT_NAME)
                .subscribe((product: Product<IProductDataV3>) => this.trackProductModelChange(product))
        );
    }

    /**
     * Set up listener for basket changes
     */
    protected setBasketChangeTracking(): void {
        this.subscriptions.add(this.basket.subscribe(() => this.timeoutService.setTimeout(() => this.trackBasketChange())));

        this.trackBasketChange();
    }

    /**
     * Set up listener for customer email changes.
     */
    protected setCustomerEmailTracking(): void {
        this.subscriptions.add(this.customerModel.obs.email$.subscribe(() => this.sendCustomerEmail()));
        this.subscriptions.add(this.customerModel.obs.status.subscribe(() => this.sendCustomerEmail()));

        this.sendCustomerEmail();
    }

    private sendCustomerEmail(): void {
        const email = this.customerModel.getSeenEmail();
        if (email && this.lastCustomerEmail !== email) {
            this.lastCustomerEmail = email;
            this.trackCustomerEmail(email);
        }
    }

    /**
     * Set up listener for impressions tracking.
     */
    protected setImpressionsTracking(): void {
        // router subscription
        this.subscriptions.add(
            this.meta.routerEvents.pipe(filter(e => e instanceof NavigationStart)).subscribe(() => this.trackImpressions())
        );
    }

    private setSearchTracking() {
        const onSearchEvent = this.timeoutService.debounce(
            (event: IEventType['dfSearch']) => {
                const request = event.data;
                const searchResult = event.result!;
                if (request.q) {
                    const numberOfResults =
                        (searchResult?.products?.length || 0) +
                        (searchResult?.categories?.length || 0) +
                        (searchResult?.pages?.length || 0);
                    this.trackSearchResult({
                        term: request.q,
                        autocomplete: request.autocomplete,
                        redirect: searchResult?.data.redirect,
                        numberOfResults: request.gtmMaxNumberOfResults
                            ? Math.min(numberOfResults, request.gtmMaxNumberOfResults)
                            : numberOfResults
                    });
                }
            },
            2000,
            false
        );
        this.app.event.success(event => {
            if (event.id === 'dfSearch') {
                onSearchEvent(event);
            }
        });
    }

    /**
     * This observes for checkout initialization complete event. This is called once the checkout is loaded
     * for the first time and it shoud track the first (landing) step.
     * @param data
     */
    onCheckoutInitAfter(data) {
        if (data.currentStepName) {
            this.trackStep(data.currentStepName, data.checkout);
        }
    }

    protected onCheckoutSubmitBefore(checkoutSubmitBeforeData: Checkout.ISubmitBeforeEventData): void {
        this.checkoutBeforeSubmitData = {
            checkoutData: cloneDeep((<any>checkoutSubmitBeforeData.checkout).data)
        };
    }

    /**
     * If customer decides to go with gues checkout or logged in checkout, this is triggered
     * @param data
     */
    onGuestStatusChanged(data): void {
        this.trackStep('account', data.checkout);
    }

    /**
     * This is triggered whenever checkout step changes.
     * @param data
     */
    onCheckoutStepChange(data): void {
        this.trackStep(data.step.name, data.checkout);
    }

    /**
     * If shipping method changes. This may be called on any checkout step, not only delivery step.
     * @param data
     */
    onShippingMethodChanged(data: Checkout.IQuoteDataShipping): void {
        const stepIndex = this.getStepIndexByName('delivery');

        if (stepIndex > 0 && data.method) {
            this.trackCheckoutOption(stepIndex, data.method);
        }
    }

    /**
     * If payment method changes. This may be called on any checkout step, not only payment step.
     * @param data
     */
    onPaymentMethodChanged(data: Checkout.ISavePaymentMethodEventData): void {
        const stepIndex = this.getStepIndexByName('payment');

        if (stepIndex > 0 && data.method) {
            this.trackCheckoutOption(stepIndex, data.method.toString());
        }
    }

    /**
     * Return step index (the one we send to GTM). Return -1 if step name invalid.
     * @param name
     */
    getStepIndexByName(name): number {
        // let's make it clear - if given step name is not registered, we consider it a mistake
        return this.checkoutStepsStructure[name] || -1;
    }

    onRegistrationRequest(event: IEventType['nexusCustomerRegister']): void {
        const data = event.data as Login.IRegisterFormData;

        if (data.newsletter !== undefined) {
            this.trackNewsletter(data.newsletter, data.email, 'register');
        }
    }

    /**
     * Track the checkout step. Based on chckout history, decide whether to track full checkout event or just
     * checkoutOption.
     * @param stepName
     * @param checkout
     */
    trackStep(stepName: string, checkout) {
        const stepIndex = this.getStepIndexByName(stepName);
        const maxStep = this.maxTrackedCheckoutStep || 0;

        if (maxStep < stepIndex) {
            // track as new step
            this.maxTrackedCheckoutStep = stepIndex;
            this.trackCheckoutStep(stepIndex, this.getCheckoutStepOptions(stepName, checkout));
        } else {
            // track as step option
            this.trackCheckoutOption(stepIndex, this.getCheckoutStepOptions(stepName, checkout));
        }
    }

    /**
     * Each checkout event has the 'options' field. The contents of this field depends on the actual step type.
     * This is also used by the checkoutOption
     * @param stepName
     * @param checkout
     */
    getCheckoutStepOptions(stepName: string, checkout) {
        switch (stepName) {
            case 'account':
                return checkout.isGuestCheckout ? 'guest' : 'logged in';
            case 'delivery':
                return checkout.data.shipping.method || 'not selected';
            case 'payment':
                return checkout.data.payment.method || 'not selected';
        }

        return '';
    }

    //////////////////////////////////////////////////////
    ///
    ///
    /// TRACKING
    ///
    ///
    //////////////////////////////////////////////////////

    /**
     * This event signifies that a user has begun a checkout.
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#begin_checkout
     * @info Ready for GA4
     */
    public trackBeginCheckout() {
        const eventData = this.getBeginCheckoutEventData();

        this.events.next(new GtmServiceBeginCheckoutEvent(eventData));

        this.push(eventData);

        this._logEvent(eventData);
    }

    /**
     * Get begin checkout event data.
     *
     * @info Ready for GA4
     */
    public getBeginCheckoutEventData(): Gtm.IEventBeginCheckout {
        return {
            event: this.eventsMap.beginCheckout,
            ecommerce: {
                currency: this.getCurrencyCode(),
                value: this.getEcommerceItemsFromBasketValue(),
                items: this.getEcommerceItemsFromBasket()
            }
        };
    }

    protected trackSavePaymentMethodBefore(data: Checkout.ISavePaymentMethodEventData): void {
        const eventData = this.getSavePaymentMethodBeforeData(data);

        this.events.next(new GtmServiceAddPaymentInfoEvent(eventData));

        this.push(eventData);

        this._logEvent(eventData);
    }

    /**
     * Get payment method event data.
     *
     * @info Ready for GA4
     */
    protected getSavePaymentMethodBeforeData(data: Checkout.ISavePaymentMethodEventData): Gtm.IEventAddPaymentInfo {
        return {
            event: this.eventsMap.addPaymentInfo,
            ecommerce: {
                currency: this.getCurrencyCode(),
                value: this.getEcommerceItemsFromBasketValue(),
                coupon: this.getEcommerceCouponCode(),
                payment_type: data.methodDetails?.title || `${data.methodDetails?.code}`,
                items: this.getEcommerceItemsFromBasket()
            }
        };
    }

    protected trackSaveShipmentMethodBefore(data: Checkout.ISaveShippingMethodEventData): void {
        const eventData = this.getSaveShipmentMethodBeforeData(data);

        this.events.next(new GtmServiceAddShipmentInfoEvent(eventData));

        this.push(eventData);

        this._logEvent(eventData);
    }

    /**
     * Get shipment method event data.
     *
     * @info Ready for GA4
     */
    protected getSaveShipmentMethodBeforeData(data: Checkout.ISaveShippingMethodEventData): Gtm.IEventAddShipmentInfo {
        return {
            event: this.eventsMap.addShipmentInfo,
            ecommerce: {
                currency: this.getCurrencyCode(),
                value: this.getEcommerceItemsFromBasketValue(),
                shipping_tier: data.methodDetails?.title || `${data.methodDetails?.code || ''}`,
                shipping_code: `${data.methodDetails?.code || ''}`,
                items: this.getEcommerceItemsFromBasket()
            }
        };
    }

    /**
     * This event signifies an item was selected from a list.
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#select_item
     * @info Ready for GA4
     */
    public trackSelectItem(data: Gtm.IEventSelectItemData): void {
        const eventData = this.getSelectItemEventData(data);

        this.events.next(new GtmServiceSelectItemEvent(eventData));

        this.push(eventData);

        this._logEvent(eventData);
    }

    /**
     * Get select item event data.
     *
     * @param data {Gtm.IEventSelectItemData} Event data.
     * @returns {Gtm.IEventSelectItem}
     */
    protected getSelectItemEventData(data: Gtm.IEventSelectItemData): Gtm.IEventSelectItem {
        return {
            event: this.eventsMap.selectItem,
            ecommerce: data
        };
    }

    /**
     * This event signifies that some content was shown to the user.
     * Use this event to discover the most popular items viewed.
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item
     * @info Ready for GA4
     */
    protected trackViewItem(model: Product): void {
        const eventData = this.getViewItemEventData(model);

        this.events.next(new GtmServiceViewItemEvent(eventData));

        this.push(eventData);

        this._logEvent(eventData);
    }

    /**
     * Get view item event data.
     *
     * @param model {Product}
     * @returns {Gtm.IEventViewItem}
     */
    protected getViewItemEventData(model: Product): Gtm.IEventViewItem {
        return {
            event: this.eventsMap.viewItem,
            ecommerce: {
                currency: this.getCurrencyCode(),
                value: this.getEcommerceItemsValue(model),
                items: this.getEcommerceItems(model)
            }
        };
    }

    /**
     * Log this event when the user has been presented with a list of  of a certain category.
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#select_item
     * @info Ready for GA4
     */
    public trackViewItemList(data: Gtm.IEventViewItemListData): void {
        const eventData = this.getViewItemListEventData(data);

        this.events.next(new GtmServiceViewItemListEvent(eventData));

        this.push(eventData);

        this._logEvent(eventData);
    }

    /**
     * Get view item event data.
     *
     * @param data {Gtm.IEventViewItemListData}
     * @returns {Gtm.IEventViewItemList}
     */
    protected getViewItemListEventData(data: Gtm.IEventViewItemListData): Gtm.IEventViewItemList {
        return {
            event: this.eventsMap.viewItemList,
            ecommerce: data
        };
    }

    /**
     * This event signifies that a user viewed their cart..
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_cart
     * @info Ready for GA4
     */
    public trackViewCart() {
        const eventData = this.getViewCartEventData();

        this.events.next(new GtmServiceViewCartEvent(eventData));

        this.push(eventData);

        this._logEvent(eventData);
    }

    /**
     * Get view cart event data.
     *
     * @info Ready for GA4
     */
    public getViewCartEventData(): Gtm.IEventViewCart {
        return {
            event: this.eventsMap.viewCart!,
            ecommerce: {
                currency: this.getCurrencyCode(),
                value: this.getEcommerceItemsFromBasketValue(),
                items: this.getEcommerceItemsFromBasket()
            }
        };
    }

    /**
     * Log this event when the user subscribe/unsubscribe newsletter.
     */
    trackNewsletter(value: any, email: string, placement?: string): void {
        const eventData = this.getNewsletterEventData(value, email, placement);

        this.events.next(new GtmServiceNewsletterEvent(eventData));

        this.push(eventData);

        this._logEvent(eventData);
    }

    /**
     * Get newsletter event data.
     *
     * @param data {Gtm.IEventNewsletterData}
     * @returns {Gtm.IEventNewsletter}
     */
    protected getNewsletterEventData(value: any, email: string, placement?: string): Gtm.IEventNewsletter {
        return {
            event: this.eventsMap.newsletter,
            ecommerce: {
                value,
                email,
                placement
            }
        };
    }

    /**
     * Track event coming from BE response
     *
     * @param eventData {Gtm.IEvent}
     *
     * @info Ready for GA4
     */
    protected trackBackendEvent(eventData: Gtm.IEvent, response?: IResponseMeta) {
        const callback = (data: Gtm.IEvent) => {
            this.events.next(new GTMServiceTrackBackendEvent(data)); // was 'gtm.trackContentView.before

            this.push(data);
        };

        // buffer checkout successs event
        // and trigger it after checkout success page is loaded
        if (eventData.event === this.eventsMap.checkoutSuccess) {
            this.checkoutSuccessBufferCallback = () => callback(eventData);
        } else {
            callback(eventData);
        }
    }

    public sendEventGA4(eventName: string, eventDetails: Gtm.IEventDataGA4 = {}, isNonInteraction: boolean = false): void {
        const event = <Gtm.IEventGA4>{
            event: this.eventsMap.eventGA4,
            ga4_event_name: eventName,
            ga4_event_data: eventDetails
        };

        this.push(event);
    }

    /**
     * Pushes content view event
     *
     * @param {string} path   Location path viewed
     * @param {Model} model  Model to track
     * @param {any} metaData Model meta daat
     */
    protected trackContentView(path: string, model?: Model, metaData?: any): void {
        const eventData: Gtm.IEventContentView = {
            event: this.eventsMap.contentView,
            'content-name': path,
            'content-count': contentCount++,
            meta: omit(metaData, 'pageType'),
            pageType: this.gtmProvider.pageType || 'other'
        };

        eventData.customer = this.getFilteredCustomerData();

        if (model && model.data) {
            eventData['content-data'] = {
                id: model.data['gtm_id'] || model.id,
                type: model.data['gtm_type'] || model.type,
                name: model.data['gtm_name'] || model.data['name'] || model.data['title'],
                custom: model.data['gtm_custom'] // was passToGtm - normalized to gtm_custom
            };

            if (model instanceof Product) {
                const productData = this.toProductData(model);
                if (productData) {
                    (<Gtm.IContentViewProduct>eventData['content-data']).product_id = productData.product_id;

                    (<Gtm.IEventContentViewProduct>eventData).ecommerce = {
                        currencyCode: this.getCurrencyCode(),
                        detail: {
                            products: [productData]
                        }
                    };
                    (<Gtm.IEventContentViewProduct>eventData).eventCategory = 'Ecommerce';
                    (<Gtm.IEventContentViewProduct>eventData).eventAction = 'Product Detail';
                }
            }
        } else {
            switch (this.meta.status) {
                case 404:
                    eventData['content-data'] = {
                        id: 0,
                        type: 'error404'
                    };
                    break;
                case 500:
                    eventData['content-data'] = {
                        id: 0,
                        type: 'error500'
                    };
                    break;
                default:
                    eventData['content-data'] = undefined;
            }
        }

        this.events.next(new GTMServiceTrackContentViewEvent(eventData, model)); // was 'gtm.trackContentView.before'

        this.push(eventData);

        // trigger checkout success buffer
        if (this.checkoutSuccessBufferCallback) {
            this.checkoutSuccessBufferCallback();
            this.checkoutSuccessBufferCallback = undefined;
        }
    }

    /**
     * Send GTM event when different product option is selected.
     *
     * @param product
     */
    protected trackProductModelChange(product: Product): void {
        if (product?.data && product?.isSimple) {
            this.push(this.getProductModelChangeEventData(product));
        }
    }

    protected getProductModelChangeEventData(product: Product) {
        const variantData = this.getVariantData(product);

        return {
            event: this.eventsMap.productOptionSelected,
            item: {
                sku: product.data!.sku,
                name: product.data!.name,
                in_stock: product.inStock ? 1 : 0,
                product_type: product.data!.type_id || 'simple',
                regular_price: product.orgPrice || product.price,
                special_price: product.orgPrice ? product.price : '',
                parent_sku: product.parent?.data?.sku,
                variant: variantData
            }
        };
    }

    /**
     * Pushes productClick event with ecommerce data about that product
     * only id enhanced ecommerce is active
     *
     * @todo Fix position param features.
     *
     * @param {model} model       Product model
     * @param {string} listName   List name
     * @param {number} position   Position on the list
     */
    trackProductClick(model: Product, listName?: string, position?: number): void {
        if (model && model.data) {
            const productData = this.toProductData(model);

            if (productData) {
                const eventData: Gtm.IEventProductClick = {
                    event: this.eventsMap.productClick,
                    eventCategory: 'Ecommerce',
                    eventAction: 'Product Click',
                    eventIsNonInteraction: false,
                    ecommerce: {
                        click: {
                            products: [productData],
                            actionField: {
                                list: listName || '',
                                position: position || 0
                            }
                        }
                    }
                };

                this.events.next(new GTMServiceTrackProductClickEvent(eventData, model));

                this.push(eventData);
            }
        }
    }

    /**
     * Pushes promotionClick event with ecommerce data.
     *
     * @param {options} IPromotionData       Promotion data model
     */
    trackPromotionClick(options: Gtm.IPromotionData): void {
        if (options) {
            const eventData: Gtm.IEventPromotionClick = {
                event: this.eventsMap.promotionClick,
                eventCategory: 'Ecommerce',
                eventAction: 'Promotion Click',
                eventIsNonInteraction: false,
                ecommerce: {
                    promoClick: {
                        promotions: [options]
                    }
                }
            };

            this.events.next(new GTMServiceTrackPromotionClickEvent(eventData));

            this.push(eventData);
        }
    }

    /**
     * Pushes promotionClick event with ecommerce data.
     *
     * @param {IPromotionData | IPromotionData[]}    promotions    Promotion data model
     */
    trackPromotionImpression(promotions: Gtm.IPromotionData | Gtm.IPromotionData[]): void {
        if (promotions) {
            promotions = Array.isArray(promotions) ? promotions : [promotions];

            const eventData: Gtm.IEventPromotionImpression = {
                event: this.eventsMap.promotionImpression,
                eventCategory: 'Ecommerce',
                eventAction: 'Promotion Impression',
                eventIsNonInteraction: false,
                ecommerce: {
                    promoView: {
                        promotions
                    }
                }
            };

            this.events.next(new GTMServiceTrackPromotionImpressionEvent(eventData));

            this.push(eventData);
        }
    }

    /**
     * Send customer data
     */
    trackCustomerUpdate(): void {
        const eventData: Gtm.IEventCustomerUpdate = {
            event: this.eventsMap.customerUpdate,
            customer: this.getFilteredCustomerData()
        };

        this.events.next(new GTMServiceTrackCustomerUpdateEvent(eventData));

        this.push(eventData);
    }

    /**
     * Make sure only allowed fields will be sent to DL
     */
    getFilteredCustomerData() {
        return pick(this.customerModel.data, this.moduleConfig.customerFields);
    }

    /**
     * Add new impressions to the buffer. Used by click tracking.
     *
     * @param impressions
     */
    public trackImpressionsBuffered(impressions: Gtm.IProductImpressionData | Gtm.IProductImpressionData[]) {
        impressions = Array.isArray(impressions) ? impressions : [impressions];

        this.productImpressionsBuffer.push(...impressions);

        this.trackImpressionsThrotled();
    }

    /**
     * Pushes promotionClick event with ecommerce data.
     */
    protected trackImpressions(): void {
        if (this.productImpressionsBuffer.length > 0) {
            let impressions = this.productImpressionsBuffer.splice(0);

            if (impressions) {
                impressions = Array.isArray(impressions) ? impressions : [impressions];

                const eventData: Gtm.IEventImpressions = this.getTrackImpressionsData(this.eventsMap.enhancedEvent, impressions);

                this.events.next(new GTMServiceTrackImpressionsEvent(eventData));

                this.push(eventData);
            }
        }
    }

    protected getTrackImpressionsData(enhancedEvent: string, impressions: Gtm.IProductImpressionData[]): Gtm.IEventImpressions {
        return {
            event: enhancedEvent,
            eventCategory: 'Ecommerce',
            eventAction: 'Impression',
            eventIsNonInteraction: true,
            ecommerce: {
                currencyCode: this.getCurrencyCode(),
                impressions: <Gtm.IProductImpressionData[]>impressions
            }
        };
    }

    /**
     * Sends information about customer email being seen.
     *
     * @param {srting|undefined} email
     */
    trackCustomerEmail(email: string | undefined): void {
        const eventData: Gtm.IEventCustomerEmail = {
            event: this.eventsMap.customerEmailProvided,
            customerEmail: email
        };

        this.events.next(new GTMServiceTrackCustomerEmailEvent(eventData));

        this.push(eventData);
    }

    /**
     * Sends checkout event
     *
     * IMPORTANT: products are added automaticllay from basket
     *
     * @see https://developers.google.com/tag-manager/enhanced-ecommerce#checkoutstep
     * @param {number} step   Checkout step to track
     * @param {string} option Step option
     */
    trackCheckoutStep(step: number, option?: string): void {
        const eventData: Gtm.IEventCheckout = {
            event: this.eventsMap.checkout,
            eventCategory: 'Ecommerce',
            eventAction: 'Checkout',
            eventIsNonInteraction: true,
            ecommerce: {
                checkout: {
                    actionField: {
                        step,
                        option
                    }
                },
                products: this.getBasketProductsData()
            }
        };

        this.events.next(new GTMServiceTrackCheckoutStepEvent(eventData));

        this.push(eventData);
    }

    /**
     * Sends checkout step changes event
     *
     * @param {number} step   Checkout step change is on
     * @param {string} option Step option
     */
    trackCheckoutOption(step: number, option: string) {
        const eventData: Gtm.IEventOptionCheckout = {
            event: this.eventsMap.checkoutOption,
            eventCategory: 'Ecommerce',
            eventAction: 'Checkout Option',
            eventIsNonInteraction: true,
            ecommerce: {
                checkout_option: {
                    actionField: {
                        step,
                        option
                    }
                }
            }
        };

        this.events.next(new GTMServiceTrackCheckoutOptionEvent(eventData));

        this.push(eventData);
    }

    /**
     * Pushes interaction event
     */
    public trackInteractionEvent(
        eventCategory: string | undefined,
        eventAction: string | undefined,
        eventLabel: string | undefined,
        eventValue: string | undefined
    ): void {
        const eventData: Gtm.IEventRegular = {
            event: this.eventsMap.interaction,
            eventCategory,
            eventAction,
            eventLabel,
            eventValue,
            eventIsNonInteraction: false
        };

        this.events.next(new GTMServiceTrackInteractionEvent(eventData));

        this.push(eventData);
    }

    /**
     * Pushes non-interaction event
     */
    trackNonInteractionEvent(eventCategory: string, eventAction: string, eventLabel: string, eventValue?: string): void {
        const eventData: Gtm.IEventRegular = {
            event: this.eventsMap.nonInteraction,
            eventCategory,
            eventAction,
            eventLabel,
            eventValue,
            eventIsNonInteraction: true
        };
        this.events.next(new GTMServiceTrackNonInteractionEvent(eventData));
        this.push(eventData);
    }

    /**
     * Track basket changes
     */
    trackBasketChange(): void {
        const qty = this.customer.basket.qty;

        // do not push initial empty basket
        if (qty ? qty !== this.lastBasketQty : this.lastBasketQty !== undefined) {
            this.lastBasketQty = qty;
            const basketData = this.customer.basket.data;
            const eventData: Gtm.IEventBasket = {
                event: this.eventsMap.basketChange,
                'basket-items': this.getBasketProductsData(),
                'basket-general': {
                    id: basketData.quote_id,
                    totals: basketData.totals ? basketData.totals : { grand_total: 0, subtotal: 0 },
                    coupon: basketData.coupon
                        ? Array.isArray(basketData.coupon)
                            ? basketData.coupon.join(', ')
                            : basketData.coupon
                        : undefined
                }
            };

            this.events.next(new GTMServiceTrackBasketChangeEvent(eventData));

            this.push(eventData);
        }
    }

    //////////////////////////////////////////////////////
    ///
    ///
    /// SEARCH
    ///
    ///
    //////////////////////////////////////////////////////

    trackSearchResult(search: { term: string; autocomplete: boolean; redirect?: string; numberOfResults: number }) {
        if (search.term) {
            const eventData: Gtm.IEventSearch = {
                event: 'search',
                search_term: search.term?.toLowerCase(),
                search_type: search.autocomplete ? 'preview' : search.redirect ? 'redirect' : 'standard',
                number_of_results: search.numberOfResults
            };
            this.push(eventData);
        }
    }

    //////////////////////////////////////////////////////
    ///
    ///
    /// EVENT PROCESSING
    ///
    ///
    //////////////////////////////////////////////////////

    /**
     * Pushes GTM event to data layer
     *
     * @param {IEvent} eventData
     */
    push(_eventData: Gtm.IEvent): void {
        if (typeof window === 'undefined') {
            return;
        }

        this.app.event.dispatch({
            id: 'dfGtmEvent',
            data: {
                eventData: _eventData
            },
            action: data => {
                const eventData = data.eventData;
                if (window.dataLayer === undefined) {
                    window.dataLayer = [];
                }

                // strip off ecommerce data if disabled
                if (eventData['ecommerce']) {
                    window.dataLayer.push({ ecommerce: undefined } as any); // without this ecommerce impressions are merged with previous data
                }

                /**
                 * Ensure the data layer event elements will not merge with previous pushes.
                 * In most cases we want to prevent the 'ecommerce' attribute to merge with old values.
                 */

                if (window.google_tag_manager) {
                    const gtmDl = find(window.google_tag_manager, (value, key) => isString(key) && (key as string).indexOf('GTM') == 0);
                    if (gtmDl) {
                        Object.keys(eventData).forEach(key => {
                            gtmDl.dataLayer.set(key, undefined);
                        });
                        //Reset general event properties
                        each(['event', 'eventCategory', 'eventAction', 'eventLabel', 'eventValue', 'ecommerce'], propertyName => {
                            if (isUndefined(eventData[propertyName])) {
                                gtmDl.dataLayer.set(propertyName, undefined);
                            }
                        });
                    }
                }

                defaults(eventData, {
                    event: undefined,
                    eventCategory: undefined,
                    eventAction: undefined,
                    eventLabel: undefined,
                    eventValue: undefined,
                    ecommerce: undefined,
                    pageType: this.gtmProvider.pageType
                });

                window.dataLayer.push(eventData);

                this.events.next(new GTMServiceEventAfter(eventData));
            }
        });
    }

    //////////////////////////////////////////////////////
    ///
    ///
    /// HELPERS
    ///
    ///
    //////////////////////////////////////////////////////

    protected getCurrencyCode(): string {
        return this.prefs.getCurrencyCode();
    }

    protected getVariantData(product: Product) {
        const variantData = {};

        if (product.parent) {
            const attrCodes = product.parent.configurableAttributeCodes;

            if (attrCodes?.length) {
                each(attrCodes, attr => {
                    variantData[attr] = optionLabelPipe(product.data![attr], attr);
                });
            }
        }

        return variantData;
    }

    protected getEcommerceAffiliation(): string {
        return 'online store';
    }

    /**
     * Retrieves coupon code from basket
     *
     * @info Ready for GA4
     */
    protected getEcommerceCouponCode(): string | undefined {
        const coupon = this.customer?.basket?.data?.coupon;

        if (coupon) {
            if (Array.isArray(coupon)) {
                return coupon.join(', ');
            }

            return coupon;
        }

        return undefined;
    }

    /**
     * Retrieves data as ecommerce items array
     *
     * @info Ready for GA4
     */
    public getEcommerceItems(model: Product | Product[]): Gtm.IEcommerceItem[] {
        if (Array.isArray(model)) {
            return <Gtm.IEcommerceItem[]>compact(model.map((model: Product, index: number) => this.toEcommerceItemData(model, index)));
        }

        return compact([this.toEcommerceItemData(model)]);
    }

    /**
     * Retrieves basket data as ecommerce items array
     *
     * @info Ready for GA4
     */
    protected getEcommerceItemsValue(model: Product): number {
        return this.toEcommerceItemData(model)?.price || 0;
    }

    /**
     * Retrieves basket data as ecommerce items array
     *
     * @info Ready for GA4
     */
    protected getEcommerceItemsFromBasket(): Gtm.IEcommerceItem[] {
        return <Gtm.IEcommerceItem[]>(
            compact(this.customer.basket.items.map((basketItem: BasketItem) => this.toEcommerceItemData(basketItem)))
        );
    }

    /**
     * Retrive value of all items in basket.
     *
     * @returns {number}
     *
     * @info Ready for GA4
     */
    protected getEcommerceItemsFromBasketValue(): number | undefined {
        return this.customer.basket.totals?.subtotal;
    }

    /**
     * Convert data to ecommerce item.
     *
     * @param model {Product | BasketItem}
     * @returns Partial<Gtm.IEcommerceItem> | undefined
     *
     * @info Ready for GA4
     */
    public toEcommerceItemData(model?: Product | BasketItem, index?: number): Gtm.IEcommerceItem | undefined {
        if (!model) {
            return undefined;
        }

        if (model instanceof BasketItem) {
            const resultData = this.toEcommerceItemData(model.product);

            if (resultData) {
                resultData.price = model.data.price;
                resultData.quantity = model.qty;
                if (index !== undefined) {
                    resultData.index = index;
                }
            }

            return resultData;
        } else {
            const data = <IProductDataV2>model.data;

            if (!data || !data.sku) {
                return undefined;
            }

            const ecommerceItem = this.getEcommerceDataFromProduct(model as Product<IProductDataV2>);

            if (index !== undefined) {
                ecommerceItem.index = index;
            }

            if (ecommerceItem.item_name) {
                return ecommerceItem;
            }

            return undefined;
        }
    }

    /**
     * Get eccomerce data from product
     *
     * @param product {Product<IProductDataV2>}
     * @returns {Partial<Gtm.IEcommerceItem>}
     *
     * @info Ready for GA4
     */
    protected getEcommerceDataFromProduct(product: Product<IProductDataV2>): Gtm.IEcommerceItem {
        const data = product.data!;

        const ecommerceItem: Gtm.IEcommerceItem = {
            currency: this.getCurrencyCode(),
            price: data.price,
            item_id: data.googleId || this.getProductId(data.sku!),
            item_name: data.name!,
            affiliation: this.getEcommerceAffiliation(),
            coupon: this.getEcommerceCouponCode(),
            item_category: data.category_path || this.getDefaultListName('/', data.breadcrumbs || []),
            ...this.getAdditionalProductData(product)
        };

        if (product.brand) {
            ecommerceItem.item_brand = product.brand;
        }

        if (product.orgPrice) {
            ecommerceItem.discount = product.orgPrice - product.price;
            ecommerceItem.sale = true;
        }

        if (!(product.isConfigurable || product.isGrouped)) {
            const variants = Object.values(this.getVariantData(product));

            if (variants.length) {
                ecommerceItem.item_variant = variants.join(', ');
            }
        }

        this.ecommerceItem.next(new GTMServiceEcommerceItem(ecommerceItem, product)); // to allow product details modification on the flight

        return ecommerceItem;
    }

    /**
     * Retrieves basket data as impressions array
     */
    getBasketProductsData(): Gtm.IProductImpressionData[] {
        return <Gtm.IProductImpressionData[]>(
            compact(this.customer.basket.items.map((basketItem: BasketItem) => this.toProductData(basketItem)))
        );
    }

    /**
     * Replaced core2::getProductImpression
     * @param {[type]} model [description]
     * @param qty
     */
    public toProductData(model?: Product | BasketItem): Gtm.IProductImpressionData | undefined {
        if (!model) {
            return undefined;
        }

        if (model instanceof BasketItem) {
            const resultData = this.toProductData(model.product);

            if (resultData) {
                resultData.price = model.data.price;
                resultData.quantity = model.qty;
            }

            return resultData;
        } else {
            const data = <IProductDataV2>model.data;

            if (!data || !data.sku) {
                return undefined;
            }

            const impressionData = this.getImpressionDataFromProduct(model as Product<IProductDataV2>);

            if (impressionData.name) {
                return impressionData;
            }

            return undefined;
        }
    }

    getImpressionDataFromProduct(product: Product<IProductDataV2>): Gtm.IProductImpressionData {
        const data = product.data!;
        const impressionData: Gtm.IProductImpressionData = {
            name: data.name!,
            id: data.googleId || this.getProductId(data.sku!),
            product_id: data.id,
            price: data.price!,
            brand: product.brand,
            image: data.image ? this.app.url.toMediaUrl(data.image) : undefined,
            url: data.url,
            category: data.category_path || this.getDefaultListName('/', data.breadcrumbs || []),
            simple_sku: product.isConfigurable || product.isGrouped ? data.first_simple_sku : data.sku,
            ...this.getAdditionalProductData(product)
        };

        if (!(product.isConfigurable || product.isGrouped)) {
            const variants = this.getVariantData(product);
            impressionData.variant = Object.values(variants).join(', ');
        }

        this.productData.next(new GTMServiceProductData(impressionData, product)); // to allow product details modification on the flighta

        return impressionData;
    }

    getAdditionalProductData(product: Product<IProductDataV2>) {
        return {};
    }

    getProductId(id: string | number): string | number {
        if (typeof id === 'string') {
            const numberVersion = parseInt(id, 10);

            if (numberVersion) {
                // idiotic
                if (numberVersion == (id as any as number)) {
                    return numberVersion;
                }
            }
        }

        return id;
    }

    /**
     * Takes default EE list name by breadcrumbs
     * @return {string}
     */
    getDefaultListName(glue: string = '>', crumbs?: ICrumb[]): string {
        return map(take(crumbs || this.meta.state?.breadcrumbs || [], 5), 'label').join(glue);
    }

    private _logEvent(eventData: Gtm.IEvent, googleUniversal = false): void {
        if (googleUniversal) {
            this.app.logger.log('%cUniversal Analytics', 'color: blue; font-weight: bold;', eventData);
        } else {
            this.app.logger.log('%cGoogle Analytics 4', 'color: salmon; font-weight: bold;', eventData);
        }
    }

    dispatchCustomEvent(event_name: string, event_data: any) {
        const event: Gtm.IEventCustom = {
            event: 'custom_event',
            event_name,
            event_data
        };

        this.push(event);
    }
}
